import React, { useContext, useEffect, useState, } from "react";
import { asMoney } from "../../utils";
import { ToolbarContext } from "../../contexts/toolbar-context";
import { Link } from 'react-router-dom';
import { useUserContext } from "../../contexts/user-context";
import { ClientCompanyGroupSelect } from "../../components/MaterialSelects/ClientCompanyGroupSelect";
import {
    Accordion,
    AccordionDetails, AccordionSummary,
    Autocomplete,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Switch,
    TextField,
    Tooltip, Typography
} from "@mui/material";
import { CurrencyField } from "../../components/CurrencyInput";
import { FixedField } from "../../components/FixedField";
import { ClientCompanyTypeSelect } from "../../components/MaterialSelects/ClientCompanyTypeSelect";
import WaitSpinner from "../../components/wait-spinner";
import FieldTable from "../../components/field-table";
import { FieldTableColumnProps } from '../../components/FieldTable/FieldTableTypes';
import { Campaign, ClientCompanyContact } from "../../generated/graphql";
import { useClientCompaniesQueryLazyQuery } from "../../generated/graphql";

const contactColums: FieldTableColumnProps<ClientCompanyContact>[] = [
    {
        header: 'Name',
        accessor: 'name',
        dataType: 'link',
        options: {
            getLink: ({ row }) => ('/contacts/' + row.id)
        }
    }
];

const campaignColumns: FieldTableColumnProps<Campaign>[] = [
    {
        header: 'Ref',
        accessor: 'job_reference',
        dataType: 'link',
        options: {
            getLink: ({ row }) => ('/campaigns/' + row.job_reference)
        }
    },
    {
        header: 'Name',
        accessor: 'name',
        dataType: 'link',
        options: {
            getLink: ({ row }) => ('/campaigns/' + row.job_reference)
        }
    }
];



const CompanyForm = ({ diaForm, waiting }) => {
    const { values, handleSubmit, onChange, isDirty, status, errors } = diaForm;
    const id = values.id;
    const baseUrl = '/companies/';
    const isNew = id === 'new';
    const toolbarContext = useContext(ToolbarContext);
    const user = useUserContext();
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [fetchCompanies, { data }] = useClientCompaniesQueryLazyQuery();

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        const inputName = e.target.name;
        // Call the generic onChange function for all fields
        onChange(e);
        // Handle specific cases for `sales_company` and `accounts_company`
        if (inputName === 'sales_company' || inputName === 'accounts_company') {
            if (inputValue.length > 1) {
                let variables = {};
    
                if (inputName === 'sales_company') {
                    variables = { salesCompany_like: `%${inputValue}%` };
                } else if (inputName === 'accounts_company') {
                    variables = { accountsCompany_like: `%${inputValue}%` };
                }
    
                // Fetch companies using the dynamically set variables
                fetchCompanies({ variables });
            } else {
                setCompanySuggestions([]); // Clear suggestions if input length <= 1
            }
        }
    };
    
    const field = {
        onChange: handleInputChange
    };

    useEffect(() => {
        const buttons = [{
            type: 'button',
            icon: 'save',
            tooltip: 'Save',
            callback: handleSubmit,
            label: 'Save',
            disabled: !isDirty
        }];
        toolbarContext.setSecondary(
            {
                primaryUrl: baseUrl,
                title: (!isNew ? values.name : 'New Company'),
                secondary: buttons,
                secondaryUrl: id
            });
    }, [isDirty, id, status]);

    useEffect(() => {
        if (data && data.clientCompanies && data.clientCompanies.data) {
            const suggestions = data.clientCompanies.data.map((company) => {
                    return {
                        sales_company: company.sales_company || null,
                        accounts_company: company.accounts_company || null,
                    };
                })
                .filter((company) => company.sales_company || company.accounts_company); // Remove entries where both are null

            setCompanySuggestions(suggestions);
        }
    }, [data]);

    return (<>
        <Accordion defaultExpanded={true}>
            <AccordionSummary>Company</AccordionSummary>
            <AccordionDetails>
                {waiting && <WaitSpinner variant="overlay" />}
                <div className="grid12">
                    <div className="grid12-6">
                        <div className="grid12">
                            <Typography variant="h5">Sales</Typography>
                            <ClientCompanyGroupSelect name="clientCompanyGroup" allowBlank={true} {...field}

                                value={values.clientCompanyGroup} {...field} label="Company Group"
                                className="grid12-span" />
                            <ClientCompanyTypeSelect name="clientType" {...field} value={values.clientType} {...field}
                                error={!!errors?.clientType}
                                label="Client Type" className="grid12-span" />
                            <Autocomplete
                                freeSolo
                                options={companySuggestions.map((company) => company.sales_company)}
                                getOptionLabel={(option) => option || ''}
                                value={values?.sales_company ?? ''}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="sales_company"
                                        label="Company"
                                        {...field}
                                        className="grid12-span"
                                        error={!!errors?.sales_company}
                                        helperText={errors?.sales_company ?? ''}
                                    />
                                )}
                            />
                            <TextField name="sales_address" {...field} value={values.sales_address ?? ''} {...field}
                                label="Address" className="grid12-span" />
                            <TextField name="sales_postcode" {...field} value={values.sales_postcode ?? ''} {...field}
                                label="Postcode" />
                            <TextField name="sales_phone" {...field} value={values.sales_phone ?? ''} {...field}
                                label="Phone" />
                            <TextField name="sales_mobile" {...field} value={values.sales_mobile ?? ''} {...field}
                                label="Mobile" />
                            <TextField name="sales_type" {...field} value={values.sales_type ?? ''} {...field} label="Type" />
                        </div>
                    </div>
                    <div className="grid12-6">
                        <div className="grid12">
                            <Typography variant="h5">Accounts</Typography>
                            <TextField name="accounts_contact" value={values.accounts_contact ?? ''} {...field}
                                label="Contact" />
                            <Autocomplete
                                freeSolo
                                options={companySuggestions.map((company) => company.accounts_company)}
                                getOptionLabel={(option) => option || ''}
                                value={values?.accounts_company ?? ''}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="accounts_company"
                                        label="Company"
                                        {...field}
                                        className="grid12-span"
                                        error={!!errors?.accounts_company}
                                        helperText={errors?.accounts_company ?? ''}
                                    />
                                )}
                            />
                            <TextField name="accounts_address" value={values.accounts_address ?? ''} {...field}
                                label="Address" />
                            <TextField name="accounts_postcode" value={values.accounts_postcode ?? ''} {...field}
                                label="Postcode" />
                            <TextField type="email" name="accounts_email" value={values.accounts_email ?? ''} {...field}
                                label="Email" />
                            <TextField type="phone" name="accounts_phone" value={values.accounts_phone ?? ''} {...field}
                                label="Phone" />
                            <TextField name="accounts_account_code" value={values.accounts_account_code ?? ''} {...field}
                                label="Sage Customer Account Ref" />
                            <TextField fullWidth={true} name="accounts_comments"
                                value={values.accounts_comments ?? ''} {...field} multiline={true} label="Comments" />
                            <Tooltip title="A campaign must be subsequently be flagged as VAT exempt">
                                <FormControlLabel
                                    control={<Switch {...field} checked={values.is_vat_exempt} name="is_vat_exempt" />}
                                    label="Campaigns can be VAT exempt" />
                            </Tooltip>
                            <FormControlLabel
                                control={<Switch {...field} checked={values.is_po_required} name="is_po_required" />}
                                label="PO Required" />
                            {
                                user.hasPermission('companies.invoice_factored') &&
                                <FormControlLabel control={<Switch {...field} checked={!!values.invoice_factored}
                                    name="invoice_factored" />} label="Invoice Factored" />
                            }
                            {user.hasPermission('companies.edit_limits') &&
                                <CurrencyField name="credit_limit" value={values.credit_limit} {...field}
                                    error={!!errors?.credit_limit} label="Credit Limit" />
                            }
                            {!user.hasPermission('companies.edit_limits') &&
                                <FixedField name="credit_limit" value={"£" + asMoney(values.credit_limit)}
                                    label="Credit Limit" />
                            }
                            <TextField
                                name="current_credit"
                                value={"£" + asMoney(values.current_credit)}
                                onChange={(event) => {
                                    const rawValue = event.target.value.replace(/[^0-9]/g, ''); 
                                    handleInputChange({ target: { name: 'current_credit', value: rawValue ? parseInt(rawValue, 10) : 0 } }); 
                                }}
                                onBlur={(event) => {
                                    let rawValue = event.target.value.replace(/[^0-9]/g, ''); 
                                    if (!rawValue) rawValue = '0'; 
                                    handleInputChange({ target: { name: 'current_credit', value: parseInt(rawValue, 10) } }); 
                                }}
                                label="Credit Used"
                            />
                        </div>
                    </div>
                </div>
            </AccordionDetails>
        </Accordion>
        <Accordion>
            <AccordionSummary>Contacts</AccordionSummary>
            <FieldTable values={values.contacts} name="contacts" readOnly={true} loading={false} columns={contactColums} />
        </Accordion>
        <Accordion>
            <AccordionSummary>Campaigns</AccordionSummary>
            <FieldTable values={values.campaigns} name="campaigns" readOnly={true} loading={false} columns={campaignColumns} />
        </Accordion></>
    );
}
export default CompanyForm;

